import { useEffect, useState } from "react";
import { useAuthStore } from "@/store/authStore";
import { deckService } from "@/services/firestore";
import { Deck } from "@/types";
import { 
  Card, 
  CardContent, 
  CardFooter, 
  CardHeader, 
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Search, 
  Download, 
  Filter,
  ArrowRight,
  BookOpen,
  Sparkles,
  User
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Helmet } from "react-helmet-async";

export default function MarketplacePage() {
  const { user } = useAuthStore();
  const [decks, setDecks] = useState<Deck[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [cloningId, setCloningId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDecks = async () => {
      try {
        const publicDecks = await deckService.getPublicDecks();
        setDecks(publicDecks);
      } catch (error) {
        console.error("Error fetching public decks:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDecks();
  }, []);

  const handleClone = async (deckId: string) => {
    if (!user) {
      navigate("/login");
      return;
    }

    setCloningId(deckId);
    try {
      const newDeckId = await deckService.cloneDeck(user.uid, deckId);
      navigate(`/decks/${newDeckId}`);
    } catch (error) {
      console.error("Error cloning deck:", error);
      alert("Failed to clone deck. Please try again.");
    } finally {
      setCloningId(null);
    }
  };

  const filteredDecks = decks.filter(deck => 
    deck.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (deck.description || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-10 max-w-6xl mx-auto pt-6 pb-20 px-4 md:px-6">
      <Helmet>
        <title>Chợ thẻ cộng đồng | Flashcard Platform</title>
      </Helmet>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-2">
        <div className="space-y-4">
          <Badge variant="secondary" className="px-4 py-1.5 text-xs font-bold uppercase tracking-widest bg-primary/10 text-primary border-none rounded-full">
            Thư viện Cộng đồng
          </Badge>
          <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-tight">Chợ thẻ <br /><span className="text-primary italic">Tri thức</span></h1>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-2xl font-medium">
            Khám phá hàng ngàn bộ thẻ chất lượng từ cộng đồng để tăng tốc hành trình học tập.
          </p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 group">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-6 w-6 text-muted-foreground group-focus-within:text-primary transition-colors" />
          <Input 
            placeholder="Tìm kiếm bộ thẻ (ví dụ: 'Tiếng Anh giao tiếp', 'Giải phẫu học')..." 
            className="pl-14 h-16 text-xl rounded-3xl border-2 focus-visible:ring-primary/20 shadow-lg shadow-black/5 transition-all"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button variant="outline" size="icon" className="h-16 w-16 rounded-3xl hidden sm:flex border-2 border-muted-foreground/20 hover:border-primary hover:text-primary transition-all">
          <Filter className="h-6 w-6" />
        </Button>
      </div>

      {/* Decks Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-[300px] rounded-3xl bg-muted animate-pulse" />
          ))}
        </div>
      ) : filteredDecks.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredDecks.map((deck) => (
            <Card key={deck.id} className="group flex flex-col overflow-hidden border-2 hover:border-primary/50 transition-all duration-300 shadow-sm hover:shadow-2xl rounded-[2rem] bg-card">
              <CardHeader className="relative p-0 aspect-[16/10] bg-muted group-hover:bg-primary/5 transition-colors flex items-center justify-center overflow-hidden">
                <div className="absolute top-4 right-4 z-10">
                  <Badge variant="secondary" className="bg-background/90 backdrop-blur-md border shadow-lg font-bold px-3 py-1 rounded-full">
                    {deck.cardCount || 0} thẻ
                  </Badge>
                </div>
                {/* Decorative background circle */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <BookOpen className="h-20 w-20 text-muted-foreground/20 group-hover:text-primary/30 group-hover:scale-125 transition-all duration-700 ease-out" />
              </CardHeader>
              <CardContent className="flex-1 p-8 space-y-4">
                <div className="flex items-center gap-2 text-xs font-bold text-muted-foreground/60 uppercase tracking-widest">
                   <User className="h-3.5 w-3.5" />
                   <span>Tác giả: {deck.userId === user?.uid ? "Tôi" : (deck.createdBy || "Cộng đồng")}</span>
                </div>
                <CardTitle className="text-2xl font-bold group-hover:text-primary transition-colors leading-tight">{deck.title}</CardTitle>
                <CardDescription className="line-clamp-3 text-base leading-relaxed text-muted-foreground/80 font-medium italic">
                  &ldquo;{deck.description || "Làm chủ kiến thức với bộ thẻ học tập được chọn lọc kỹ càng này."}&rdquo;
                </CardDescription>
              </CardContent>
              <CardFooter className="p-8 pt-0 flex gap-3">
                <Link 
                  to={`/decks/${deck.id}`} 
                  className={cn(buttonVariants({ variant: "outline", size: "lg" }), "flex-1 rounded-2xl border-2 font-bold h-14 hover:bg-muted/50")}
                >
                  Xem qua
                </Link>
                <Button 
                  size="lg" 
                  className="flex-1 rounded-2xl shadow-xl shadow-primary/20 font-bold h-14"
                  onClick={() => deck.id && handleClone(deck.id)}
                  disabled={cloningId === deck.id}
                >
                  {cloningId === deck.id ? "Đang tải..." : (
                    <>
                      <Download className="mr-2 h-5 w-5" /> Sao chép
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="p-6 rounded-full bg-muted mb-6">
            <Search className="h-10 w-10 text-muted-foreground/30" />
          </div>
          <h3 className="text-2xl font-bold mb-2">Không tìm thấy bộ thẻ</h3>
          <p className="text-muted-foreground max-w-sm mb-8">
            Thử thay đổi từ khóa tìm kiếm hoặc quay lại sau để xem nội dung mới từ cộng đồng.
          </p>
          <Button variant="outline" onClick={() => setSearchQuery("")}>Xóa tìm kiếm</Button>
        </div>
      )}

      {/* Featured/Promotion Section */}
      {!searchQuery && (
        <div className="mt-24 p-12 md:p-20 rounded-[3rem] bg-zinc-900 text-white relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent opacity-50 group-hover:opacity-70 transition-opacity" />
          <Sparkles className="absolute -top-10 -right-10 h-80 w-80 text-primary/10 rotate-12 group-hover:rotate-45 transition-transform duration-1000" />
          <div className="relative z-10 max-w-2xl">
              <Badge className="bg-primary text-white border-none px-4 py-1.5 rounded-full font-bold uppercase tracking-widest mb-6">Sống là học hỏi</Badge>
             <h2 className="text-4xl md:text-6xl font-black mb-6 leading-tight">Chia sẻ là <br /> <span className="text-primary italic">Yêu thương</span></h2>
             <p className="text-zinc-400 text-xl mb-10 leading-relaxed font-medium">
               Bạn đã tạo ra một bộ thẻ tuyệt vời? Hãy chia sẻ nó với cộng đồng để giúp hàng ngàn người khác học tập dễ dàng hơn. Sự đóng góp của bạn là vô giá!
             </p>
             <Link to="/dashboard" className={cn(buttonVariants({ size: "lg" }), "bg-white text-black hover:bg-zinc-200 border-none rounded-2xl font-bold h-16 px-10 text-xl")}>
               Chia sẻ bộ thẻ của bạn <ArrowRight className="ml-2 h-6 w-6" />
             </Link>
          </div>
        </div>
      )}
    </div>
  );
}
